import { lazy, Suspense, useEffect, useRef } from 'react'
import { lazyWithRetry } from './lib/lazyWithRetry'
import { Routes, Route, Navigate, useLocation as useRouterLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from './lib/supabaseClient'
import { useAuthStore } from './stores/authStore'
import { configureQueue, initQueue, resumeQueue } from './lib/photoUploadQueue'
import { useLocation } from './hooks/useLocation'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { LoadingScreen } from './components/common/LoadingSpinner'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { UploadIndicator } from './components/common/UploadIndicator'
import { logger } from './utils/logger'
import { bootElapsed } from './utils/bootTiming'
import type { Session } from '@supabase/supabase-js'
import type { User as AppUser, UserRole } from './types/database'

// Decode the JWT access token (no signature check — we trust Supabase
// since this token came from the client SDK after a Supabase round trip).
function decodeJwtClaims(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1]
    // JWT base64 is URL-safe; normalize and decode.
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

// Build an AppUser from JWT custom claims injected by the
// custom_access_token_hook (migration 044). Returns null when claims
// haven't been issued yet — caller should fall back to fetchAppUser.
//
// Boot-critical fields (id, email, name, role, status) come from claims;
// rarely-used fields (phone, company, notes, sms timestamps, verified_at)
// default to null — the admin views that need them fetch fresh anyway.
function appUserFromSession(session: Session): AppUser | null {
  const claims = decodeJwtClaims(session.access_token)
  if (!claims || typeof claims.user_role !== 'string' || typeof claims.user_status !== 'string') {
    return null
  }
  return {
    id: session.user.id,
    email: session.user.email ?? null,
    name: (claims.user_name as string | null) ?? null,
    role: claims.user_role as UserRole,
    status: claims.user_status as 'pending' | 'approved' | 'denied',
    phone: null,
    company: null,
    notes: null,
    profile_photo_url: null,
    sms_opt_in_at: null,
    sms_opt_out_at: null,
    verified_at: null,
    created_at: '',
    deleted_at: null,
  }
}

const LoginPage   = lazyWithRetry(() => import('./components/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const ProjectList = lazyWithRetry(() => import('./components/projects/ProjectList').then(m => ({ default: m.ProjectList })))
const JoinPage    = lazyWithRetry(() => import('./components/join/JoinPage').then(m => ({ default: m.JoinPage })))
const InstallScreen = lazyWithRetry(() => import('./components/install/InstallScreen').then(m => ({ default: m.InstallScreen })))

// Lazy-loaded routes — not needed on first paint
const LogViewer = import.meta.env.DEV
  ? lazy(() => import('./components/dev/LogViewer').then(m => ({ default: m.LogViewer })))
  : null
const ProjectDetail        = lazyWithRetry(() => import('./components/projects/ProjectDetail').then(m => ({ default: m.ProjectDetail })))
const CameraCapture        = lazyWithRetry(() => import('./components/camera/CameraCapture').then(m => ({ default: m.CameraCapture })))
const MarkupEditor         = lazyWithRetry(() => import('./components/markup/MarkupEditor').then(m => ({ default: m.MarkupEditor })))
const ReportScreen         = lazyWithRetry(() => import('./components/reports/ReportScreen').then(m => ({ default: m.ReportScreen })))
const ReportReadyScreen    = lazyWithRetry(() => import('./components/reports/ReportReadyScreen').then(m => ({ default: m.ReportReadyScreen })))
const ReportsHistoryScreen = lazyWithRetry(() => import('./components/reports/ReportsHistoryScreen').then(m => ({ default: m.ReportsHistoryScreen })))
const AdminPanel           = lazyWithRetry(() => import('./components/admin/AdminPanel').then(m => ({ default: m.AdminPanel })))
const TrashView            = lazyWithRetry(() => import('./components/photos/TrashView').then(m => ({ default: m.TrashView })))
const FeedScreen           = lazyWithRetry(() => import('./components/feed/FeedScreen').then(m => ({ default: m.FeedScreen })))
const SchedulePage         = lazyWithRetry(() => import('./components/schedule/SchedulePage').then(m => ({ default: m.SchedulePage })))
const MessagesScreen        = lazyWithRetry(() => import('./components/messages/MessagesScreen').then(m => ({ default: m.MessagesScreen })))
const ConversationView      = lazyWithRetry(() => import('./components/messages/ConversationView').then(m => ({ default: m.ConversationView })))
const ProjectScheduleScreen = lazyWithRetry(() => import('./components/projects/ProjectScheduleScreen').then(m => ({ default: m.ProjectScheduleScreen })))
const ProjectMessagesScreen = lazyWithRetry(() => import('./components/projects/ProjectMessagesScreen').then(m => ({ default: m.ProjectMessagesScreen })))

// Preload all lazy route chunks once we're online so they're cached
// by the SW for offline use. Without this, a freshly-installed PWA
// that goes offline before navigating to a route hits a hard
// "Importing a module script failed" error when the user finally
// navigates there. Fire-and-forget after the app is interactive.
function preloadRouteChunks() {
  if (typeof navigator === 'undefined' || !navigator.onLine) return
  // Imports are de-duped by the runtime; lazyWithRetry-wrapped ones
  // share the same module promise as the actual route's lazy import.
  Promise.allSettled([
    import('./components/projects/ProjectDetail'),
    import('./components/camera/CameraCapture'),
    import('./components/markup/MarkupEditor'),
    import('./components/reports/ReportScreen'),
    import('./components/reports/ReportReadyScreen'),
    import('./components/reports/ReportsHistoryScreen'),
    import('./components/admin/AdminPanel'),
    import('./components/photos/TrashView'),
    import('./components/feed/FeedScreen'),
    import('./components/schedule/SchedulePage'),
    import('./components/messages/MessagesScreen'),
    import('./components/messages/ConversationView'),
    import('./components/projects/ProjectScheduleScreen'),
    import('./components/projects/ProjectMessagesScreen'),
  ]).catch(() => { /* silent — preload is best-effort */ })
}

// Ask the browser to mark our origin storage bucket as persistent.
// On iOS standalone PWAs WebKit ties grants (camera, etc.) to the
// website data store and may evict the store under storage pressure;
// a persisted bucket is not eligible for eviction. The hypothesis we're
// testing: durable storage helps the camera grant survive cold opens.
//
// Surface the result via logger so it shows up in Admin → Logs.
async function requestPersistentStorage() {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.persisted) {
      logger.info('storage', 'persist API unavailable')
      return
    }
    const already = await navigator.storage.persisted()
    if (already) {
      logger.info('storage', 'persisted: already true')
      return
    }
    if (!navigator.storage.persist) {
      logger.info('storage', 'persist() not supported, only persisted()')
      return
    }
    const granted = await navigator.storage.persist()
    logger.info('storage', `persist() returned ${granted}`)
  } catch (err) {
    logger.warn('storage', 'persist request threw', { error: String(err) })
  }
}

// Boot diagnostic — captures the full per-launch environment snapshot.
// Logs at .metric so it lands in Admin → Logs. The two synthetic counters
// give us observability we wouldn't otherwise have:
//   - localStorage bootCount survives across launches if WebKit hasn't
//     evicted the data store. Resets to 1 after a true wipe (Settings →
//     Safari → Website Data → remove).
//   - sessionStorage 'sessionAlive' sentinel is per-WebContent-process
//     on iOS. Empty on boot ⇒ this is a fresh process (true reap or
//     install); populated ⇒ WebKit kept us suspended and resumed the
//     same process. This is the only observable proxy for whether iOS
//     actually reaped vs just suspended us.
export type CameraBootSnapshot = {
  bootCount: number
  bootCountLooksFresh: boolean
  msSinceLastBoot: number | null
  permissionStateCamera: string
  permissionStateGeo: string
  storagePersisted: boolean | null
  storageQuotaMB: number | null
  storageUsageMB: number | null
}

async function logBootDiagnostic(): Promise<void> {
  try {
    if (typeof navigator === 'undefined') return

    const nowIso = new Date().toISOString()
    const displayMode = (typeof window !== 'undefined' && window.matchMedia)
      ? (window.matchMedia('(display-mode: standalone)').matches ? 'standalone'
        : window.matchMedia('(display-mode: minimal-ui)').matches ? 'minimal-ui'
        : window.matchMedia('(display-mode: browser)').matches ? 'browser'
        : 'unknown')
      : 'unknown'
    // navigator.standalone is iOS-specific and not in the TS lib types
    const isStandaloneIOS = (navigator as unknown as { standalone?: boolean }).standalone ?? null

    // bootCount: increment, write back
    let bootCount = 1
    let msSinceLastBoot: number | null = null
    let bootCountLooksFresh = true
    try {
      const stored = window.localStorage.getItem('diag.bootCount')
      const lastIso = window.localStorage.getItem('diag.lastBootIso')
      if (stored && /^\d+$/.test(stored)) {
        bootCount = parseInt(stored, 10) + 1
        bootCountLooksFresh = false
      }
      if (lastIso) {
        const last = Date.parse(lastIso)
        if (!Number.isNaN(last)) msSinceLastBoot = Date.now() - last
      }
      window.localStorage.setItem('diag.bootCount', String(bootCount))
      window.localStorage.setItem('diag.lastBootIso', nowIso)
    } catch { /* private mode etc. — ignore */ }

    // sessionStorage sentinel: per-process. Empty ⇒ fresh WebContent process.
    let processIsFresh = true
    try {
      const sentinel = window.sessionStorage.getItem('diag.sessionAlive')
      if (sentinel) processIsFresh = false
      else window.sessionStorage.setItem('diag.sessionAlive', nowIso)
    } catch { /* ignore */ }

    // Permission states (camera + geolocation)
    async function readPerm(name: PermissionName): Promise<string> {
      if (!navigator.permissions?.query) return 'unsupported'
      try {
        const status = await navigator.permissions.query({ name })
        return status.state
      } catch {
        return 'errored'
      }
    }
    const [permCamera, permGeo] = await Promise.all([
      readPerm('camera' as PermissionName),
      readPerm('geolocation' as PermissionName),
    ])

    // Storage state
    let storagePersisted: boolean | null = null
    let storageQuotaMB: number | null = null
    let storageUsageMB: number | null = null
    if (navigator.storage?.persisted) {
      try { storagePersisted = await navigator.storage.persisted() } catch { /* ignore */ }
    }
    if (navigator.storage?.estimate) {
      try {
        const est = await navigator.storage.estimate()
        storageQuotaMB = est.quota != null ? Math.round(est.quota / 1e6) : null
        storageUsageMB = est.usage != null ? Math.round(est.usage / 1e6) : null
      } catch { /* ignore */ }
    }

    logger.metric('session-boot', 'boot diagnostic', {
      bootCount,
      bootCountLooksFresh,
      msSinceLastBoot,
      processIsFresh,
      displayMode,
      isStandaloneIOS,
      permissionStateCamera: permCamera,
      permissionStateGeo: permGeo,
      storagePersisted,
      storageQuotaMB,
      storageUsageMB,
      ua: navigator.userAgent,
    })
  } catch (err) {
    logger.warn('session-boot', 'boot diagnostic threw', { error: String(err) })
  }
}

// Rich camera permission observability. The native PermissionStatus.change
// event hasn't fired in any of our captured field data (probably because the
// state changes happen across WebContent process boundaries or while iOS has
// us frozen). Augment with manual polling on visibility/focus/page-lifecycle
// events plus a 30s tick while visible. We deduplicate aggressively — only
// log when state differs from the last observed value — so this is quiet
// during steady state and chatty exactly when the state transitions.
async function setupCameraPermissionsObservability(): Promise<void> {
  try {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return
    const status = await navigator.permissions.query({ name: 'camera' as PermissionName })
    let last: string = status.state
    let pollTimer: number | null = null

    async function checkAndLogIfChanged(trigger: string) {
      try {
        const s = await navigator.permissions.query({ name: 'camera' as PermissionName })
        const current = s.state
        if (current !== last) {
          logger.metric('permissions', `${trigger}: camera ${last} -> ${current}`, {
            trigger,
            from: last,
            to: current,
            visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
          })
          last = current
        }
      } catch { /* ignore */ }
    }

    // Native change event — keep this even though it appears to fire
    // unreliably on iOS standalone PWA. Doesn't hurt.
    status.addEventListener('change', () => {
      const next = status.state
      if (next !== last) {
        logger.metric('permissions', `native-event: camera ${last} -> ${next}`, {
          trigger: 'native-event',
          from: last,
          to: next,
          visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        })
        last = next
      }
    })

    // App becomes visible after the user returns from Safari, another app,
    // or unlocks the phone. iOS may have frozen + thawed our WebContent
    // process during the gap, or the OS may have flipped TCC underneath us.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        void checkAndLogIfChanged(
          document.visibilityState === 'visible' ? 'visibility-visible' : 'visibility-hidden'
        )
      })
    }

    // Focus / blur — sometimes fire even when visibilitychange doesn't (e.g.
    // a brief switch to a different app on iPad split view).
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => { void checkAndLogIfChanged('focus') })
      window.addEventListener('blur', () => { void checkAndLogIfChanged('blur') })
      // bfcache resume / page-lifecycle freeze-thaw
      window.addEventListener('pageshow', (e) => {
        void checkAndLogIfChanged((e as PageTransitionEvent).persisted ? 'pageshow-bfcache' : 'pageshow')
      })
      window.addEventListener('pagehide', () => { void checkAndLogIfChanged('pagehide') })
    }

    // Periodic poll while document is visible — catches silent decay or
    // restoration that doesn't coincide with any user-observable event.
    function startPoll() {
      if (pollTimer != null) return
      pollTimer = window.setInterval(() => {
        if (typeof document === 'undefined' || document.visibilityState === 'visible') {
          void checkAndLogIfChanged('periodic-30s')
        }
      }, 30_000) as unknown as number
    }
    function stopPoll() {
      if (pollTimer != null) {
        window.clearInterval(pollTimer)
        pollTimer = null
      }
    }
    if (typeof document !== 'undefined') {
      if (document.visibilityState === 'visible') startPoll()
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') startPoll()
        else stopPoll()
      })
    }
  } catch (err) {
    logger.warn('permissions', 'observability setup threw', { error: String(err) })
  }
}

function RouteLogger() {
  const location = useRouterLocation()
  const prevPath = useRef<string | null>(null)
  useEffect(() => {
    const from = prevPath.current ?? '(launch)'
    const to = location.pathname
    logger.info('router', `navigate`, { from, to })
    prevPath.current = to
  }, [location.pathname])
  return null
}

export default function App() {
  const { isLoading, setUser, setAppUser, setIsLoading } = useAuthStore()
  const queryClient = useQueryClient()

  // Wire the photo upload queue to react-query so it can invalidate
  // the photos cache when a background upload completes, then resume
  // any uploads that were in flight when the page last unloaded.
  useEffect(() => {
    configureQueue({ queryClient })
    void initQueue()

    // Resume the upload queue whenever the app returns to the foreground.
    // iOS may have frozen our process, missed the 'online' event, or left
    // the boot-time drain with nothing kicking it. Bringing the app forward
    // is the one moment we know the user cares and the network is likely
    // live, so revive any stuck/failed entries and re-drain. resumeQueue is
    // a cheap no-op when there's nothing pending.
    const onForeground = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        void resumeQueue('app-foreground')
      }
    }
    document.addEventListener('visibilitychange', onForeground)
    window.addEventListener('focus', onForeground)
    window.addEventListener('pageshow', onForeground)
    // Ask iOS / WebKit to mark our origin storage bucket as persistent so
    // it isn't eligible for eviction. Hypothesis: a non-evicted store
    // helps the standalone-PWA getUserMedia grant survive cold opens.
    // Log the result so it shows up in the Admin → Logs tab during testing.
    void requestPersistentStorage()
    // logBootDiagnostic is deferred into the auth-resolution path (below)
    // because app_logs RLS requires auth.uid() — firing here always loses
    // the row silently. setupCameraPermissionsObservability attaches
    // long-lived event handlers that fire log entries later, so it can
    // start here; flips it logs will land once the user is signed in.
    void setupCameraPermissionsObservability()
    // Preload lazy route chunks on idle so they're cached for offline
    // use. Without this, a freshly-installed PWA that goes offline
    // before navigating to /markup (or any other lazy route) hits a
    // hard chunk-import failure when the user finally tries.
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => preloadRouteChunks(), { timeout: 3000 })
    } else {
      setTimeout(preloadRouteChunks, 1500)
    }
    // NOTE: do NOT call clearLazyRetryFlag here. It used to be called
    // on every App mount, which cleared the sessionStorage retry
    // guard before a second chunk-load failure could be detected as
    // "already retried" — producing an infinite reload loop on any
    // chunk that consistently fails. sessionStorage already clears
    // when the user closes the PWA, so the flag's natural lifetime
    // is fine without us manually clearing it.
    return () => {
      document.removeEventListener('visibilitychange', onForeground)
      window.removeEventListener('focus', onForeground)
      window.removeEventListener('pageshow', onForeground)
    }
  }, [queryClient])

  // Tracks the userId we've already initiated a fetch for. Both the
  // onAuthStateChange(INITIAL_SESSION) listener and the explicit getSession()
  // fallback race to call fetchAppUser on cold boot — without this, we hit
  // the users table twice (each ~700ms cold). Reset on SIGNED_OUT so re-login
  // for the same user still refreshes.
  const fetchedAppUserIdRef = useRef<string | null>(null)

  // Initialize location on app load
  useLocation()

  useEffect(() => {
    let isMounted = true

    // Single funnel for setting appUser. Both the auth state listener
    // (INITIAL_SESSION/SIGNED_IN) and the explicit getSession() fallback
    // can fire on the same boot; without this guard each one would log
    // its own 'appUser from claims' + 'auth ready' metric. The ref
    // dedupes by userId so the metrics fire exactly once.
    function resolveAppUser(session: Session) {
      if (fetchedAppUserIdRef.current === session.user.id) return
      fetchedAppUserIdRef.current = session.user.id

      const fromClaims = appUserFromSession(session)
      if (fromClaims) {
        logger.metric('boot', 'appUser from claims', { elapsed_since_boot_ms: bootElapsed() })
        setAppUser(fromClaims)
        logger.metric('boot', 'auth ready', { elapsed_since_boot_ms: bootElapsed(), source: 'claims' })
        setIsLoading(false)
        // Boot diagnostic must wait for auth to be ready — app_logs RLS
        // requires auth.uid() IS NOT NULL on insert, so firing earlier
        // gets silently dropped.
        void logBootDiagnostic()
      } else {
        // Reset the ref so fetchAppUser's own retry path can succeed —
        // its internal guard tracks the same id.
        fetchedAppUserIdRef.current = null
        fetchAppUser(session.user.id)
      }
    }

    // Set up auth state listener FIRST - this handles all auth events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return

        logger.info('auth', `auth state: ${event}`, { email: session?.user?.email ?? null })

        if (event === 'SIGNED_OUT') {
          fetchedAppUserIdRef.current = null
          setUser(null)
          setAppUser(null)
          setIsLoading(false)
        } else if (
          (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') &&
          session?.user
        ) {
          setUser(session.user)
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
            resolveAppUser(session)
          }
        }
      }
    )

    // Check for OAuth callback tokens in URL hash (implicit flow fallback)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const accessToken = hashParams.get('access_token')
    const refreshToken = hashParams.get('refresh_token')

    if (accessToken && refreshToken) {
      logger.info('auth', 'OAuth callback received — setting session from URL hash')
      // Clear the hash from URL
      window.history.replaceState(null, '', window.location.pathname)

      // Set the session - this will trigger onAuthStateChange
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      })
    } else {
      // Check for existing session - triggers INITIAL_SESSION event on newer Supabase
      // For older versions, we need to handle it manually
      const tSession = performance.now()
      supabase.auth.getSession().then(({ data: { session } }) => {
        logger.metric('boot', 'getSession', { hasSession: !!session?.user, elapsed_since_boot_ms: bootElapsed() }, Math.round(performance.now() - tSession))
        if (!isMounted) return

        if (session?.user) {
          // Session exists - set user state (onAuthStateChange may not fire for existing sessions)
          setUser(session.user)
          resolveAppUser(session)
        } else {
          // No session
          logger.metric('boot', 'auth ready (no session)', { elapsed_since_boot_ms: bootElapsed() })
          setIsLoading(false)
        }
      })
    }

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  async function fetchAppUser(userId: string, retryCount = 0): Promise<void> {
    const MAX_RETRIES = 3
    const RETRY_DELAY = 500

    // Skip if we've already initiated a fetch for this user. Internal
    // retries pass retryCount > 0 and bypass this guard.
    if (retryCount === 0) {
      if (fetchedAppUserIdRef.current === userId) return
      fetchedAppUserIdRef.current = userId
    }

    try {
      const qStart = performance.now()
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()
      const qDuration = Math.round(performance.now() - qStart)

      logger.metric('boot', 'fetchAppUser', { retry: retryCount, elapsed_since_boot_ms: bootElapsed() }, qDuration)

      if (qDuration > 3000) {
        logger.warn('supabase-query', 'slow query: fetch app user', { duration_ms: qDuration, userId })
      }

      if (error && error.code === 'PGRST116') {
        // User doesn't exist in users table yet — create as pending for admin approval
        logger.info('auth', 'new user — provisioning pending account', { userId })
        const { data: { user: authUser } } = await supabase.auth.getUser()

        if (authUser?.email) {
          const newUser = {
            id: authUser.id,
            email: authUser.email,
            name: authUser.user_metadata?.full_name ||
                  authUser.user_metadata?.name ||
                  authUser.email?.split('@')[0] || 'User',
            role: 'user' as const,
            status: 'pending' as const,
          }

          const { data: createdUser, error: createError } = await supabase
            .from('users')
            .insert(newUser)
            .select()
            .single()

          if (createError) {
            if (retryCount < MAX_RETRIES) {
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
              return fetchAppUser(userId, retryCount + 1)
            }
            logger.error('auth', 'user provisioning failed after retries', { userId, error: createError.message })
          } else if (createdUser) {
            logger.info('auth', 'new user provisioned', { userId, email: authUser.email })
            setAppUser(createdUser)
            logger.metric('boot', 'auth ready (new user)', { elapsed_since_boot_ms: bootElapsed() })
            setIsLoading(false)
            void logBootDiagnostic()
            return
          }
        }
      } else if (error) {
        if (retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
          return fetchAppUser(userId, retryCount + 1)
        }
        logger.error('supabase-query', 'fetch app user failed after retries', { userId, error: error.message, code: error.code })
        throw error
      } else if (data) {
        setAppUser(data)
        logger.metric('boot', 'auth ready', { elapsed_since_boot_ms: bootElapsed() })
        setIsLoading(false)
        void logBootDiagnostic()
        return
      }
    } catch (error) {
      console.error('Error fetching user:', error)
      logger.error('auth', 'fetchAppUser exception', { userId, error: String(error) })
    }

    setIsLoading(false)
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <ErrorBoundary>
    <Suspense fallback={<LoadingScreen />}>
      <RouteLogger />
      <UploadIndicator />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/install" element={<InstallScreen />} />
        {/* Legacy/alias — marketing CTA used to point here */}
        <Route path="/signup" element={<Navigate to="/join" replace />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ProjectList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/project/:projectId"
          element={
            <ProtectedRoute>
              <ProjectDetail />
            </ProtectedRoute>
          }
        />

        <Route
          path="/project/:projectId/camera"
          element={
            <ProtectedRoute>
              <CameraCapture />
            </ProtectedRoute>
          }
        />

        <Route
          path="/project/:projectId/photo/:photoId/markup"
          element={
            <ProtectedRoute>
              <MarkupEditor />
            </ProtectedRoute>
          }
        />

        <Route
          path="/project/:projectId/schedule"
          element={
            <ProtectedRoute requireAdmin>
              <ProjectScheduleScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path="/project/:projectId/messages"
          element={
            <ProtectedRoute requireAdmin>
              <ProjectMessagesScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path="/project/:projectId/report-screen"
          element={
            <ProtectedRoute>
              <ReportScreen />
            </ProtectedRoute>
          }
        />

        <Route
          path="/report-ready/:reportId"
          element={
            <ProtectedRoute>
              <ReportReadyScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/:reportId"
          element={
            <ProtectedRoute>
              <ReportReadyScreen />
            </ProtectedRoute>
          }
        />

        {/* Feed */}
        <Route
          path="/feed"
          element={
            <ProtectedRoute>
              <FeedScreen />
            </ProtectedRoute>
          }
        />

        {/* Trash */}
        <Route
          path="/trash"
          element={
            <ProtectedRoute>
              <TrashView />
            </ProtectedRoute>
          }
        />

        {/* Reports history */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <ReportsHistoryScreen />
            </ProtectedRoute>
          }
        />

        {/* Schedule */}
        <Route
          path="/schedule"
          element={
            <ProtectedRoute requireAdmin>
              <SchedulePage />
            </ProtectedRoute>
          }
        />

        {/* Messages — admin-only for v1.3 Phase 1 */}
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <MessagesScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages/:conversationId"
          element={
            <ProtectedRoute>
              <ConversationView />
            </ProtectedRoute>
          }
        />
        {/* Magic-link entry — unprotected. ConversationView reads the `mt`
            query param and uses server endpoints (server-mediated, scoped
            to this conversation only). If they try to navigate elsewhere
            they hit the normal auth gate. */}
        <Route
          path="/m/:conversationId"
          element={<ConversationView />}
        />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPanel />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {LogViewer && <Suspense fallback={null}><LogViewer /></Suspense>}
    </Suspense>
    </ErrorBoundary>
  )
}
